import React, { FC, useEffect, useState } from "react";
import Popup from "../../../../components/common/model/Popup";
import { Alert, DialogContent, DialogContentText } from "@mui/material";
import CustomButton from "../../../../components/common/button/CustomButton";
import useStyles from "../../../../components/common/model/styles";
import closeIcon from "../../../../assets/closeModel.png";
import { useDispatch, useSelector } from "react-redux";
import {
  checkCustomerStorageAction,
  clearCustomerStorageCheckResponse,
} from "../../../../store/reducers/contractStorageSlice";
import {
  addStorageAction,
  clearStorageDetails,
  getSingleStorageAction,
  setCreatedStorageResponse,
  setStorageResponse,
} from "../../../../store/reducers/storageSlice";
import ManageStorage from "./ManageStorage";
import StorageContract from "./contracts/storageContract";
import { setNotificationAction } from "../../../../store/reducers/mmymSlice";
import { mergeDateAndTime } from "../../../../hooks/helper";

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

interface RemovalCustomer {
  id: string;
  name: string;
  email: string;
  countryCode: string;
  phone: string;
  address: string;
  postcode: string;
}

interface ExistingStorageCustomer {
  id?: string;
  name?: string;
  email?: string;
  phone?: string;
}

interface CustomerStorageResponse {
  leadId: string;
  leadStatus: string;
  removalCustomer: RemovalCustomer;
  existingCustomerFound: boolean;
  existingStorageCustomer: ExistingStorageCustomer | null;
  activeContractsCount: number;
  prompt: string;
  actions: string[];
}

/* -------------------------------------------------------------------------- */
/* Storage Address                                                            */
/* -------------------------------------------------------------------------- */

interface StorageAddress {
  buildingNumber: string | null;
  street: string;
  town: string;
  county: string | null;
  postcode: string;
  country: string | null;
  purchaseStatus: string;
  houseSize: string;
  distance: number;
  volume: number;
  volumeUnit: string;
}

/* -------------------------------------------------------------------------- */
/* Selected Storage Lead                                                      */
/* -------------------------------------------------------------------------- */

interface SelectedStorageLead {
  generatedId: string;

  referrerId: string;
  clientReferrerId: string;

  customer: {
    name: string;
    phone: string;
    prefix: string;
    accountName: string;
    email: string;
    alternatePhone: string;
    alternateCountryCode: string;
    countryCode: string;
  };

  collectionAddress: StorageAddress[];
  deliveryAddress: StorageAddress[];

  followUpDate: string;
  followUpEndDate:
    | {
        start: string;
        end: string;
      }
    | "";

  movingOnDate: string | string[];
  movingOnEndDate: string;
  movingDate: string;
  packingOnDate: string;

  moveType: string;

  returningDistance: number;

  customerNotes: string;

  advertId: string;
  promoCode: string;
  promoSource: string;

  salesPersonId: string;
  salesPersonName: string;

  leadTypeId: string;

  batch: string;
  inceptBatch: string;
  leadDate: string;
}

/* -------------------------------------------------------------------------- */
/* Props                                                                      */
/* -------------------------------------------------------------------------- */

interface AlertCheckStorageModalProps {
  open: boolean;
  onHide: () => void;
  setCheckStorageModal?: (value: boolean) => void;
  submitHandler?: () => void;
}

/* -------------------------------------------------------------------------- */
/* Helper                                                                     */
/* -------------------------------------------------------------------------- */

const createAddress = (): StorageAddress => ({
  buildingNumber: null,
  street: "",
  town: "",
  county: null,
  postcode: "",
  country: null,
  purchaseStatus: "",
  houseSize: "",
  distance: 0,
  volume: 0,
  volumeUnit: "m3",
});

/* -------------------------------------------------------------------------- */
/* Map API Lead -> Storage Lead                                               */
/* -------------------------------------------------------------------------- */

const mapLeadToStorageLead = (lead: any): SelectedStorageLead | null => {
  if (!lead) {
    return null;
  }

  /* ------------------------------------------------------------------------ */
  /* Collection Address                                                       */
  /* ------------------------------------------------------------------------ */

  const collectionAddress: StorageAddress[] =
    Array.isArray(lead?.collection_address) &&
    lead.collection_address.length > 0
      ? lead.collection_address.map((address: any) => ({
          buildingNumber:
            address?.building_number ?? address?.buildingNumber ?? null,

          street: address?.street ?? "",

          town: address?.town ?? "",

          county: address?.county ?? address?.county_name ?? null,

          postcode: address?.postcode ?? "",

          country: address?.country ?? null,

          purchaseStatus:
            address?.purchase_status ?? address?.purchaseStatus ?? "",

          houseSize: address?.house_size ?? address?.houseSize ?? "",

          distance: Number(address?.distance) || 0,

          volume: Number(address?.volume) || 0,

          volumeUnit: address?.volume_unit ?? address?.volumeUnit ?? "m3",
        }))
      : [createAddress()];

  /* ------------------------------------------------------------------------ */
  /* Delivery Address                                                         */
  /* ------------------------------------------------------------------------ */

  const deliveryAddress: StorageAddress[] =
    Array.isArray(lead?.delivery_address) && lead.delivery_address.length > 0
      ? lead.delivery_address.map((address: any) => ({
          buildingNumber:
            address?.building_number ?? address?.buildingNumber ?? null,

          street: address?.street ?? "",

          town: address?.town ?? "",

          county: address?.county ?? address?.county_name ?? null,

          postcode: address?.postcode ?? "",

          country: address?.country ?? null,

          purchaseStatus:
            address?.purchase_status ?? address?.purchaseStatus ?? "",

          houseSize: address?.house_size ?? address?.houseSize ?? "",

          distance: Number(address?.distance) || 0,

          volume: Number(address?.volume) || 0,

          volumeUnit: address?.volume_unit ?? address?.volumeUnit ?? "m3",
        }))
      : [createAddress()];

  /* ------------------------------------------------------------------------ */
  /* Final Selected Lead                                                      */
  /* ------------------------------------------------------------------------ */

  return {
    generatedId: lead?.generated_id ?? "",

    referrerId: lead?.referrer_id ?? "",

    clientReferrerId: lead?.client_referrer_id ?? "",

    customer: {
      name: lead?.customer_name ?? "",

      phone: lead?.customer_phone
        ? String(lead.customer_phone).replace(/^(\+91|\+44)/, "")
        : "",

      prefix: lead?.customer_prefix ?? "",

      accountName: lead?.customer_account_name ?? "",

      email: lead?.customer_email ?? "",

      alternatePhone: lead?.customer_alternate_phone
        ? String(lead.customer_alternate_phone).replace(/^(\+91|\+44)/, "")
        : "",

      alternateCountryCode: lead?.customer_alternate_country_code ?? "+44",

      countryCode: lead?.customer_country_code ?? "+44",
    },

    collectionAddress,

    deliveryAddress,

    followUpDate: lead?.follow_up_date ?? "",

    followUpEndDate:
      lead?.follow_up_date && lead?.follow_up_end_date
        ? {
            start: lead.follow_up_date,
            end: lead?.follow_up_end_date ?? "",
          }
        : "",

    movingOnDate: lead?.moving_on_end_date
      ? [lead.moving_on_date, lead?.moving_on_end_date].filter(Boolean)
      : "",

    movingOnEndDate: lead?.moving_on_end_date ?? "",

    movingDate: lead?.moving_on_date ?? "",

    packingOnDate: lead?.packing_on_date ?? "",

    moveType: lead?.type_of_move ?? "",

    returningDistance: Number(lead?.returning_distance) || 0,

    customerNotes: lead?.customer_notes ?? "",

    advertId: lead?.advert_id ?? "",

    promoCode: lead?.promo_code ?? "",

    promoSource: lead?.promo_source ?? "",

    salesPersonId: lead?.sales_person_id ?? "",

    salesPersonName: lead?.sales_person_name ?? "",

    leadTypeId: lead?.lead_type_id ?? "",

    batch: lead?.batch ?? "",

    inceptBatch: lead?.incept_batch ?? "",

    leadDate: lead?.lead_date ?? "",
  };
};

const createStoragePayload = (lead: SelectedStorageLead) => {
  const { start, end } = lead.followUpEndDate || {};
  const movingOnDate = Array.isArray(lead.movingOnDate)
    ? lead.movingOnDate[0]
    : lead.movingOnDate || "";
  const movingOnEndDate = Array.isArray(lead.movingOnDate)
    ? lead.movingOnDate[1] || ""
    : lead.movingOnEndDate || "";

  return {
    referrerId: lead.referrerId,
    clientReferrerId: lead.clientReferrerId,
    removalLeadId: lead.generatedId,
    customer: lead.customer,
    collectionAddress: lead.collectionAddress,
    deliveryAddress: lead.deliveryAddress,
    followUpDate:
      mergeDateAndTime(lead.followUpDate, start) || lead.followUpDate || "",
    followUpEndDate: mergeDateAndTime(lead.followUpDate, end) || "",
    movingOnDate,
    movingOnEndDate,
    movingDate: lead.movingDate,
    packingOnDate: lead.packingOnDate,
    moveType: lead.moveType,
    returningDistance: lead.returningDistance,
    customerNotes: lead.customerNotes,
    advertId: lead.advertId,
    promoCode: lead.promoCode,
    promoSource: lead.promoSource,
    salesPersonId: lead.salesPersonId,
    salesPersonName: lead.salesPersonName,
    leadTypeId: lead.leadTypeId,
    batch: lead.batch,
    inceptBatch: lead.inceptBatch,
    leadDate: lead.leadDate,
  };
};

const getStorageId = (response: any): string | number | null => {
  const storage =
    response?.lead ??
    response?.storage ??
    response?.data ??
    response;

  return (
    storage?.leadId ??
    storage?.storage_lead_id ??
    storage?.storageLeadId ??
    storage?.storageId ??
    storage?.id ??
    storage?._id ??
    null
  );
};

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

const AlertCheckStorageModal: FC<AlertCheckStorageModalProps> = ({
  open,
  onHide,
  submitHandler,
  setCheckStorageModal,
}) => {
  const classes = useStyles();

  const { leadDetails } = useSelector((state: any) => state.lead);
  const {
    response: storageResponseState,
    createdStorageResponse,
  } = useSelector((state: any) => state.storage);

  const { customerStorageResponse } = useSelector(
    (state: any) => state.contractStorage,
  );

  const dispatch = useDispatch();

  const [manageStorageOpen, setManageStorageOpen] = useState(false);
  const [showContract, setShowContract] = useState(false);

  /* ------------------------------------------------------------------------ */
  /* Close Alert Modal                                                        */
  /* ------------------------------------------------------------------------ */

  const closeModel = () => {
    setCheckStorageModal?.(false);
    setShowContract(false);
    dispatch(clearCustomerStorageCheckResponse());
    onHide();
    dispatch(setNotificationAction({ message: [], severity: "error" }));
  };

  /* ------------------------------------------------------------------------ */
  /* Check Storage Customer API                                               */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (leadDetails?.lead?.generated_id && open) {
      dispatch(
        checkCustomerStorageAction({
          leadId: leadDetails.lead.generated_id,
        }),
      );
    }
  }, [dispatch, leadDetails?.lead?.generated_id, open]);

  useEffect(() => {
    if (!storageResponseState?.addStorage || !createdStorageResponse) return;
    const storageId = getStorageId(createdStorageResponse);
    dispatch(setStorageResponse(false));
    dispatch(setCreatedStorageResponse(null));
    if (storageId !== null) {
      dispatch(clearStorageDetails());
      dispatch(getSingleStorageAction({ storageId }));
      setShowContract(true);
    } else {
      dispatch(
        setNotificationAction({
          message: ["Storage was created but its ID was not returned."],
          severity: "error",
        }),
      );
    }
  }, [
    createdStorageResponse,
    dispatch,
    storageResponseState?.addStorage,
  ]);

  /* ------------------------------------------------------------------------ */
  /* Storage Response                                                         */
  /* ------------------------------------------------------------------------ */

  const storageResponse =
    customerStorageResponse as CustomerStorageResponse | null;

  const actions: string[] = storageResponse?.actions ?? [];

  /* ------------------------------------------------------------------------ */
  /* Action Checks                                                            */
  /* ------------------------------------------------------------------------ */

  const showCreateNewCustomer = actions.includes("create_new_customer");

  const showUseExistingCustomer = actions.includes("use_existing_customer");

  const showCreateSeparateAccount = actions.includes("create_separate_account");

  const showCreateNewContract = actions.includes("create_new_contract");

  /* ------------------------------------------------------------------------ */
  /* Open Manage Storage With Lead                                            */
  /* ------------------------------------------------------------------------ */

  const openManageStorage = () => {
    const mappedLead = mapLeadToStorageLead(leadDetails?.lead);

    console.log("Original leadDetails.lead:", leadDetails?.lead);

    console.log("Mapped selectedStorageLead:", mappedLead);

    if (!mappedLead) {
      console.error("Unable to map lead details for Storage.");
      return;
    }

    /*
     * Store complete mapped lead.
     */
    /*
     * Close Alert modal.
     */
    // setCheckStorageModal?.(false);
    // onHide();

    /*
     * Open ManageStorage.
     */
    setManageStorageOpen(true);
  };

  /* ------------------------------------------------------------------------ */
  /* Create New Customer                                                      */
  /* ------------------------------------------------------------------------ */

  const handleCreateNewCustomer = () => {
    const mappedLead = mapLeadToStorageLead(leadDetails?.lead);
    if (!mappedLead) return;

    dispatch(setStorageResponse(false));
    dispatch(setCreatedStorageResponse(null));
    dispatch(clearStorageDetails());
    dispatch(addStorageAction(createStoragePayload(mappedLead)));
  };

  /* ------------------------------------------------------------------------ */
  /* Use Existing Customer                                                    */
  /* ------------------------------------------------------------------------ */

  const handleUseExistingCustomer = () => {
    console.log("Use Existing Customer clicked");

    setShowContract(true);
  };

 

  /* ------------------------------------------------------------------------ */
  /* Create New Contract                                                      */
  /* ------------------------------------------------------------------------ */

  const handleCreateNewContract = () => {
    console.log("Create New Contract clicked");

    openManageStorage();
  };

  /* ------------------------------------------------------------------------ */
  /* Manage Storage Close                                                     */
  /* ------------------------------------------------------------------------ */

  const handleManageStorageClose = () => {
    setManageStorageOpen(false);
    setShowContract(false);
  };

  /* ------------------------------------------------------------------------ */
  /* UI                                                                       */
  /* ------------------------------------------------------------------------ */

  return (
    <>
      {/* ================================================================== */}
      {/* Manage Storage                                                     */}
      {/* ================================================================== */}

      {manageStorageOpen && (
        <ManageStorage
          selectLead={leadDetails?.lead}
          open={manageStorageOpen}
          onHide={handleManageStorageClose}
          title="Add Storage"
          screen="removal"
        />
      )}
      {/* manage contract */}
      {showContract && (
        <StorageContract show={showContract} setShow={setShowContract} />
      )}
      {/* ================================================================== */}
      {/* Storage Alert Modal                                                */}
      {/* ================================================================== */}

      <Popup
        open={open && !manageStorageOpen && !showContract}
        title="Alert"
        onHide={closeModel}
        fullWidth
        maxWidth="sm"
        aria-describedby="alert-dialog-slide-description"
        header={
          <>
            <img
              onClick={closeModel}
              src={closeIcon}
              className={classes.closeicons}
              alt="close"
            />
          </>
        }
        footer={
          <>
            {/* ------------------------------------------------------------ */}
            {/* No                                                            */}
            {/* ------------------------------------------------------------ */}

            <CustomButton variant="outlined" title="No" onClick={closeModel} />

            {/* ------------------------------------------------------------ */}
            {/* Create New Customer                                           */}
            {/* ------------------------------------------------------------ */}

            {showCreateNewCustomer && (
              <CustomButton
                title="Create New Customer"
                onClick={handleCreateNewCustomer}
              />
            )}

            {/* ------------------------------------------------------------ */}
            {/* Use Existing Customer                                         */}
            {/* ------------------------------------------------------------ */}

            {showUseExistingCustomer && (
              <CustomButton
                title="Use Existing Customer"
                onClick={handleUseExistingCustomer}
              />
            )}

            {/* ------------------------------------------------------------ */}
            {/* Create Separate Account                                       */}
            {/* ------------------------------------------------------------ */}

            {showCreateSeparateAccount && (
              <CustomButton
                title="Create Separate Account"
                onClick={handleCreateNewCustomer}
              />
            )}

            {/* ------------------------------------------------------------ */}
            {/* Create New Contract                                           */}
            {/* ------------------------------------------------------------ */}

            {showCreateNewContract && (
              <CustomButton
                title="Create New Contract"
                onClick={handleCreateNewContract}
              />
            )}
          </>
        }
      >
        <DialogContent sx={{ padding: "0px" }}>
          <DialogContentText>
            <Alert
              sx={{
                bgcolor: "#eeecef",
                fontSize: "16px",
              }}
              severity="info"
            >
              {storageResponse?.prompt || "Checking storage information..."}
            </Alert>
          </DialogContentText>
        </DialogContent>
      </Popup>
    </>
  );
};

export default AlertCheckStorageModal;
